/**
 * "Available to play."
 *
 * Being online says you have the app open. It does not say whether you want
 * to be asked, which game, or for how long - so a list of green dots is not
 * actually a list of people you can play with tonight. This is the missing
 * half: a game, a length of time, and an optional word about it.
 *
 * It expires by itself. Nothing here is ever left advertising an evening that
 * ended two days ago, which is the failure that makes every "looking for
 * group" flag on every other app worthless after a week.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Availability } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Button, Field, Modal, useToast } from './ui';
import { Select } from './Select';
import { GameMark } from './GameMark';

/** The lengths on offer. Anything longer is really "tonight". */
const SPANS = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 240, label: 'Tonight' }
] as const;

/** "47m left", or "2h 5m left". */
export function timeLeft(until: number): string {
  const ms = until - Date.now();
  if (ms <= 0) return 'over';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return mins + 'm left';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h + 'h' + (m ? ' ' + m + 'm' : '') + ' left';
}

/** Your own offer, or null. Re-reads as it counts down so it disappears on time. */
export function useMyAvailability(): Availability | null {
  const mine = useSession((s) => s.me?.available ?? null);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!mine) return;
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [mine]);
  return mine && mine.until > Date.now() ? mine : null;
}

/** Somebody's offer, drawn as a chip. */
export function AvailableTag({ available, className }: { available?: Availability | null; className?: string }) {
  const catalog = useSession((s) => s.catalog);
  if (!available || available.until <= Date.now()) return null;
  const game = available.gameId ? catalog?.games.find((g) => g.id === available.gameId) : null;
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full bg-accent-soft px-2.5 py-[3px] text-[11.5px] font-bold text-accent',
        className
      )}
      title={available.note || undefined}
    >
      <Icon name="bolt" size={12} className="flex-none" />
      <span className="truncate">
        Free{game ? ' for ' + game.name : ' to play'} · {timeLeft(available.until)}
      </span>
    </span>
  );
}

/**
 * Setting it.
 *
 * The games you have on your profile come first, because "what am I free to
 * play" is almost always one of those.
 */
export function AvailabilitySheet({ onClose }: { onClose: () => void }) {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const refresh = useSession((s) => s.refresh);
  const queryClient = useQueryClient();
  const toast = useToast();

  const current = me?.available && me.available.until > Date.now() ? me.available : null;
  const [gameId, setGameId] = useState(current?.gameId ?? me?.games?.[0]?.id ?? '');
  const [minutes, setMinutes] = useState<number>(60);
  const [note, setNote] = useState(current?.note ?? '');
  const [busy, setBusy] = useState(false);

  const mine = new Set((me?.games ?? []).map((g) => g.id));
  const games = [...(catalog?.games ?? [])].sort((a, b) => (mine.has(b.id) ? 1 : 0) - (mine.has(a.id) ? 1 : 0));

  async function save(clear?: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch('/api/me', {
        available: clear ? null : { gameId: gameId || null, minutes, note: note.trim() }
      });
      await refresh();
      void queryClient.invalidateQueries({ queryKey: ['friends'] });
      toast(clear ? 'You are no longer marked available' : 'Everyone can see you are free to play', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Available to play"
      subtitle="Say what you are up for and for how long. It clears itself when the time is up."
      onClose={onClose}
      footer={
        <>
          {current ? <Button kind="plain" disabled={busy} onClick={() => void save(true)}>Clear it</Button> : null}
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : current ? 'Update' : "I'm available"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {current ? (
          <p className="m-0 flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-inset px-3 py-2.5 text-[13px] text-dim">
            <Icon name="clock" size={15} className="flex-none text-accent" />
            You are already marked available — {timeLeft(current.until)}.
          </p>
        ) : null}

        <Field label="Game">
          <Select
            value={gameId}
            onChange={setGameId}
            options={[
              { value: '', label: 'Anything' },
              ...games.map((g) => ({ value: g.id, label: g.name + (mine.has(g.id) ? ' · yours' : ''), icon: g.icon, color: g.color }))
            ]}
          />
        </Field>

        <div>
          <span id="av-span" className="mb-2 block text-[13.5px] font-semibold text-ink">For how long</span>
          <div role="radiogroup" aria-labelledby="av-span" className="flex flex-wrap gap-1.5">
            {SPANS.map((s) => {
              const on = s.minutes === minutes;
              return (
                <button
                  key={s.minutes}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setMinutes(s.minutes)}
                  className={cn(
                    'h-[36px] rounded-full px-4 text-[13px] font-semibold outline-none',
                    'transition-[background-color,color,box-shadow] duration-[160ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                  )}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <Field label="A word about it" hint="Optional. “Ranked only”, “need one more”, “chill games”.">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 60))}
            placeholder="Ranked, mic on, no rage"
            spellCheck={false}
            className={cn(
              'h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[14px] text-ink outline-none',
              'placeholder:text-ghost transition-colors focus:border-accent'
            )}
          />
        </Field>

        <div className="flex items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-inset p-3">
          {gameId ? <GameMark id={gameId} size={34} /> : <span className="grid h-[34px] w-[34px] place-items-center rounded-[10px] bg-surface-3 text-mute"><Icon name="dice" size={18} /></span>}
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-bold text-ink">This is what people will see</span>
            <span className="block truncate text-[12px] text-mute">
              Free{gameId ? ' for ' + (catalog?.games.find((g) => g.id === gameId)?.name ?? 'a game') : ' to play'} · {SPANS.find((s) => s.minutes === minutes)!.label.toLowerCase()}
              {note.trim() ? ' · ' + note.trim() : ''}
            </span>
          </span>
        </div>
      </div>
    </Modal>
  );
}
